import { Response as ApplicationResponse } from '../../api/typegen/client/application/get';
import { Response as ApplicationStatusResponse } from '../../api/typegen/client/application/check-status/get';
import { useEffect, useRef, useState } from 'react';
import {
  useGetClientApplication,
  useGetClientApplicationCheckStatus,
} from '../../api';
import { useNavigate } from 'react-router-dom';
import useCrossDomainLoginRedirect from '../../hooks/useCrossDomainLoginRedirect';

import { getFetchHeaders } from '../../utils/fetchHeaders';
import { routes } from '../../routes';
import { usePolling } from '../../hooks/usePolling';
import { useSeon } from '../../hooks/useSeon';

const POLLING_INTERVAL = 5000;
const MAX_ATTEMPTS = 3;

export function useResolutionResolverPhoneVerification() {
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { fetch: fetchGetClientApplicationCheckStatus } =
    useGetClientApplicationCheckStatus();
  const { sendSeonFraudInfo } = useSeon();
  const { crossDomainLoginRedirect } = useCrossDomainLoginRedirect();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationResponse | null>(
    null,
  );
  const applicationStatusRef = useRef<ApplicationStatusResponse | null>(null);
  const [attempts, setAttempts] = useState(1);

  const fetchApplicationStatus = async () => {
    try {
      const response = await fetchGetClientApplicationCheckStatus();
      if (response) applicationStatusRef.current = response;
      else throw new Error();
    } catch {
      if (attempts > MAX_ATTEMPTS) {
        clientApplicationCheckStatusPolling.stopPolling();
        crossDomainLoginRedirect('dashboard', false);
      } else {
        setAttempts(attempts + 1);
      }
    }
  };

  const applicationPolling = usePolling(
    () =>
      fetchGetApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      }),
    (response: ApplicationResponse) => {
      setApplication(response);
    },
  );
  const clientApplicationCheckStatusPolling = usePolling(
    fetchApplicationStatus,
  );

  /**
   * Effects
   */
  useEffect(() => {
    clientApplicationCheckStatusPolling.startPolling(POLLING_INTERVAL);

    return () => clientApplicationCheckStatusPolling.stopPolling();
  }, []);

  useEffect(() => {
    (function handleCanPollApplication() {
      if (
        applicationStatusRef.current?.completed !== true ||
        applicationPolling.isEnabled
      )
        return;

      clientApplicationCheckStatusPolling.stopPolling();
      applicationPolling.startPolling(POLLING_INTERVAL);
    })();
  }, [
    clientApplicationCheckStatusPolling.pollingAttempts,
    applicationStatusRef.current?.completed,
    applicationPolling.isEnabled,
  ]);

  useEffect(() => {
    (function handleApplicationResolution() {
      if (!application) return;

      applicationPolling.stopPolling();
      handleNavigateByApplicationResolution(application);
    })();
  }, [application?.resolution]);

  /**
   * Functions
   */
  async function handleNavigateByApplicationResolution(
    application: ApplicationResponse,
  ) {
    const nextRoute =
      application?.resolution === 'REJECTED'
        ? routes.FirstLoan_Rejected
        : routes.FirstLoan_OnlineBank_Verify;

    try {
      await sendSeonFraudInfo();
    } catch {}
    navigate(nextRoute);
  }
}
